import type { DamageZone } from "../types"

// "Where's the damage?" in two easy steps: which side of the car (a picture
// tile), then which part of it (big chips). Every part is one of the 14
// damage spots the walk, the server and the shop already use. Pure.

export type CarArea = "front" | "back" | "driver" | "passenger" | "roof"

export const AREAS: readonly CarArea[] = ["front", "back", "driver", "passenger", "roof"]

export interface AreaPart {
  zone: DamageZone
  // Short, inside its area ("Front door" under "Driver's side"). Always part
  // of the spot's full name, which is what screen readers announce.
  label: string
}

export const AREA_PARTS: Record<CarArea, readonly AreaPart[]> = {
  front: [
    { zone: "front-bumper", label: "Bumper" },
    { zone: "hood", label: "Hood" },
    { zone: "windshield", label: "Windshield" },
  ],
  back: [
    { zone: "rear-bumper", label: "Bumper" },
    { zone: "trunk", label: "Trunk" },
  ],
  driver: [
    { zone: "front-fender-left", label: "Front fender" },
    { zone: "front-door-left", label: "Front door" },
    { zone: "rear-door-left", label: "Back door" },
    { zone: "quarter-panel-left", label: "Rear fender" },
  ],
  passenger: [
    { zone: "front-fender-right", label: "Front fender" },
    { zone: "front-door-right", label: "Front door" },
    { zone: "rear-door-right", label: "Back door" },
    { zone: "quarter-panel-right", label: "Rear fender" },
  ],
  roof: [{ zone: "roof", label: "Roof" }],
}

const AREA_OF = new Map<DamageZone, CarArea>(
  AREAS.flatMap((area) => AREA_PARTS[area].map((p) => [p.zone, area] as const)),
)

export function areaOf(zone: DamageZone): CarArea {
  const area = AREA_OF.get(zone)
  if (!area) throw new Error(`No area for ${zone}`)
  return area
}

// Areas that have at least one picked part, in tile order.
export function areasWith(zones: readonly DamageZone[]): CarArea[] {
  return AREAS.filter((area) => AREA_PARTS[area].some((p) => zones.includes(p.zone)))
}

// One-part areas (the roof) are picked by the tile itself.
export function singlePart(area: CarArea): DamageZone | null {
  const parts = AREA_PARTS[area]
  return parts.length === 1 ? parts[0].zone : null
}
